import { useEffect, useState } from 'react';
import { writeAuditLog } from '@/lib/auditLog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  AlertCircle,
  RefreshCw,
  FileWarning,
  XCircle,
  CheckCircle,
  Search,
  Loader2,
  Trash2,
  Check,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAgentMemory } from '@/hooks/useAgentMemory';
import { SEO } from '@/components/SEO';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface UnmatchedTransaction {
  id: string;
  date: string;
  amount: number;
  narration: string;
  bank_name: string | null;
}

interface HighRiskInvoice {
  id: string;
  vendor: string;
  invoice_no: string;
  date: string;
  amount: number;
  risk_score: number;
  flag: string | null;
}

export default function Exceptions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { logInteraction, updateOutcome } = useAgentMemory();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('unmatched');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [unmatchedTxns, setUnmatchedTxns] = useState<UnmatchedTransaction[]>([]);
  const [highRiskInvoices, setHighRiskInvoices] = useState<HighRiskInvoice[]>([]);
  
  const [selectedTxns, setSelectedTxns] = useState<Set<string>>(new Set());
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);

    const [txnResult, invoiceResult] = await Promise.all([
      supabase
        .from('bank_transactions')
        .select('id, date, amount, narration, bank_name')
        .eq('user_id', user.id)
        .eq('status', 'unmatched')
        .order('date', { ascending: false }),
      supabase
        .from('invoices')
        .select('id, vendor, invoice_no, date, amount, risk_score, flag')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .gt('risk_score', 50)
        .order('risk_score', { ascending: false }),
    ]);

    if (txnResult.data) setUnmatchedTxns(txnResult.data);
    if (invoiceResult.data) setHighRiskInvoices(invoiceResult.data);
    
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  // Bulk selection handlers
  const toggleTxnSelection = (id: string) => {
    setSelectedTxns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const toggleInvoiceSelection = (id: string) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const selectAllTxns = () => {
    if (selectedTxns.size === filteredTxns.length) {
      setSelectedTxns(new Set());
    } else {
      setSelectedTxns(new Set(filteredTxns.map(t => t.id)));
    }
  };

  const selectAllInvoices = () => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map(i => i.id)));
    }
  };

  // Bulk actions
  const handleBulkDeleteTxns = async () => {
    if (selectedTxns.size === 0) return;
    const ids = Array.from(selectedTxns);
    
    const { error } = await supabase
      .from('bank_transactions')
      .delete()
      .in('id', ids);

    if (!error) {
      writeAuditLog({ action: 'transaction.bulk_deleted', resource_type: 'bank_transaction', details: { count: ids.length, ids } });
      toast.success(`Deleted ${selectedTxns.size} transactions`);
      setSelectedTxns(new Set());
      fetchData();
    } else {
      toast.error('Failed to delete transactions');
    }
  };

  const handleBulkReconcile = async () => {
    if (selectedTxns.size === 0) return;
    navigate('/sync');
  };

  const handleBulkApproveInvoices = async () => {
    if (selectedInvoices.size === 0) return;

    // Log bulk action for learning
    const interactionId = await logInteraction({
      interaction_type: 'bulk_action',
      input: { action: 'bulk_approve', count: selectedInvoices.size },
      context: { page: 'exceptions', invoiceIds: Array.from(selectedInvoices) },
      outcome: 'pending'
    });

    const { error } = await supabase
      .from('invoices')
      .update({ status: 'approved' })
      .in('id', Array.from(selectedInvoices));

    if (!error) {
      if (interactionId) {
        await updateOutcome(interactionId, 'success', 'positive');
      }
      writeAuditLog({ action: 'invoice.approved', resource_type: 'invoice', details: { bulk: true, count: selectedInvoices.size, ids: Array.from(selectedInvoices) } });
      toast.success(`Approved ${selectedInvoices.size} invoices`);
      setSelectedInvoices(new Set());
      fetchData();
    } else {
      if (interactionId) {
        await updateOutcome(interactionId, 'failed', 'negative');
      }
      toast.error('Failed to approve invoices');
    }
  };

  const handleBulkBlockInvoices = async () => {
    if (selectedInvoices.size === 0) return;

    // Log bulk action for learning
    const interactionId = await logInteraction({
      interaction_type: 'bulk_action',
      input: { action: 'bulk_block', count: selectedInvoices.size },
      context: { page: 'exceptions', invoiceIds: Array.from(selectedInvoices) },
      outcome: 'pending'
    });

    const { error } = await supabase
      .from('invoices')
      .update({ status: 'blocked' })
      .in('id', Array.from(selectedInvoices));

    if (!error) {
      if (interactionId) {
        await updateOutcome(interactionId, 'success', 'negative');
      }
      writeAuditLog({ action: 'invoice.blocked', resource_type: 'invoice', details: { bulk: true, count: selectedInvoices.size, ids: Array.from(selectedInvoices) } });
      toast.success(`Blocked ${selectedInvoices.size} invoices`);
      setSelectedInvoices(new Set());
      fetchData();
    } else {
      if (interactionId) {
        await updateOutcome(interactionId, 'failed', 'neutral');
      }
      toast.error('Failed to block invoices');
    }
  };

  // Single item actions
  const handleApproveInvoice = async (id: string) => {
    const { error } = await supabase
      .from('invoices')
      .update({ status: 'approved' })
      .eq('id', id);

    if (!error) {
      writeAuditLog({ action: 'invoice.approved', resource_type: 'invoice', resource_id: id });
      toast.success('Invoice approved');
      fetchData();
    }
  };

  const handleBlockInvoice = async (id: string) => {
    const { error } = await supabase
      .from('invoices')
      .update({ status: 'blocked' })
      .eq('id', id);

    if (!error) {
      writeAuditLog({ action: 'invoice.blocked', resource_type: 'invoice', resource_id: id });
      toast.success('Invoice blocked');
      fetchData();
    }
  };

  // Filtering
  const filteredTxns = unmatchedTxns.filter(t =>
    t.narration.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.bank_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredInvoices = highRiskInvoices.filter(i =>
    i.vendor.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.invoice_no.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRiskBadge = (score: number) => {
    if (score >= 80) return <Badge variant="destructive" className="rounded-xl">{score}% Risk</Badge>;
    if (score >= 60) return <Badge className="bg-warning text-warning-foreground rounded-xl">{score}% Risk</Badge>;
    return <Badge variant="secondary" className="rounded-xl">{score}% Risk</Badge>;
  };

  const totalExceptions = unmatchedTxns.length + highRiskInvoices.length;

  return (
    <div className="min-h-full bg-background">
      <SEO title="Exceptions - Sentinel" description="Resolve unmatched items and flagged invoices" />

      <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-3xl bg-destructive/10 shadow-card">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Exceptions</h1>
              <p className="text-sm text-muted-foreground">Items that need your attention</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {totalExceptions > 0 && (
              <Badge variant="destructive" className="font-bold rounded-2xl px-4 py-1">
                {totalExceptions} total
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="rounded-2xl">
              <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </motion.div>

        {/* Summary Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-4"
        >
          <Card 
            className={cn(
              "p-5 rounded-3xl cursor-pointer transition-all hover:shadow-card-hover",
              activeTab === 'unmatched' && "ring-2 ring-primary"
            )}
            onClick={() => setActiveTab('unmatched')}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-primary/10">
                <RefreshCw className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">{unmatchedTxns.length}</div>
                <p className="text-xs text-muted-foreground">Unmatched</p>
              </div>
            </div>
          </Card>
          <Card 
            className={cn(
              "p-5 rounded-3xl cursor-pointer transition-all hover:shadow-card-hover",
              activeTab === 'highrisk' && "ring-2 ring-destructive"
            )}
            onClick={() => setActiveTab('highrisk')}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-destructive/10">
                <FileWarning className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">{highRiskInvoices.length}</div>
                <p className="text-xs text-muted-foreground">Flagged</p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Search */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="relative"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Search by vendor, narration, or invoice number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-14 rounded-3xl text-base"
          />
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 rounded-3xl p-1.5 h-auto">
            <TabsTrigger value="unmatched" className="flex items-center gap-2 rounded-2xl py-3">
              <RefreshCw className="h-4 w-4" />
              Unmatched
              {unmatchedTxns.length > 0 && (
                <Badge variant="secondary" className="ml-1 rounded-xl">{unmatchedTxns.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="highrisk" className="flex items-center gap-2 rounded-2xl py-3">
              <FileWarning className="h-4 w-4" />
              Flagged
              {highRiskInvoices.length > 0 && (
                <Badge variant="destructive" className="rounded-xl">{highRiskInvoices.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Unmatched Transactions */}
          <TabsContent value="unmatched" className="mt-6 space-y-4">
            {/* Bulk Actions */}
            <AnimatePresence>
              {selectedTxns.size > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Card className="p-4 bg-primary/5 border-primary/20 rounded-3xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <span className="text-sm font-medium">{selectedTxns.size} selected</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => setSelectedTxns(new Set())} className="rounded-2xl">
                          Clear
                        </Button>
                        <Button size="sm" onClick={handleBulkReconcile} className="rounded-2xl">
                          <ArrowRight className="h-4 w-4 mr-1" />
                          Reconcile
                        </Button>
                        <Button variant="destructive" size="sm" onClick={handleBulkDeleteTxns} className="rounded-2xl">
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredTxns.length === 0 ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <Card className="p-12 text-center rounded-3xl border-dashed">
                  <Sparkles className="h-16 w-16 text-success mx-auto mb-6" />
                  <h3 className="text-xl font-semibold text-foreground mb-3">All Matched!</h3>
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    All transactions have been reconciled. Import more data to continue.
                  </p>
                  <Button variant="outline" className="mt-6 rounded-2xl" onClick={() => navigate('/sync')}>
                    Import Data
                  </Button>
                </Card>
              </motion.div>
            ) : (
              <div className="space-y-3">
                {/* Select All */}
                <div className="flex items-center gap-2 px-4 py-2">
                  <Checkbox
                    checked={selectedTxns.size === filteredTxns.length && filteredTxns.length > 0}
                    onCheckedChange={selectAllTxns}
                  />
                  <span className="text-sm text-muted-foreground">Select all</span>
                </div>

                <AnimatePresence mode="popLayout">
                  {filteredTxns.map((txn, idx) => (
                    <motion.div
                      key={txn.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <Card className={cn(
                        "p-5 rounded-3xl transition-all hover:shadow-card-hover",
                        selectedTxns.has(txn.id) && "ring-2 ring-primary bg-primary/5"
                      )}>
                        <div className="flex items-start gap-4">
                          <Checkbox
                            checked={selectedTxns.has(txn.id)}
                            onCheckedChange={() => toggleTxnSelection(txn.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-medium text-foreground line-clamp-1">{txn.narration}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                  <span>{formatDate(txn.date)}</span>
                                  {txn.bank_name && (
                                    <>
                                      <span>•</span>
                                      <span>{txn.bank_name}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className={cn(
                                'font-bold text-right shrink-0',
                                txn.amount >= 0 ? 'text-success' : 'text-foreground'
                              )}>
                                {txn.amount >= 0 ? '+' : ''}{formatCurrency(txn.amount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>

          {/* High-Risk Invoices */}
          <TabsContent value="highrisk" className="mt-6 space-y-4">
            {/* Bulk Actions */}
            <AnimatePresence>
              {selectedInvoices.size > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Card className="p-4 bg-primary/5 border-primary/20 rounded-3xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <span className="text-sm font-medium">{selectedInvoices.size} selected</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => setSelectedInvoices(new Set())} className="rounded-2xl">
                          Clear
                        </Button>
                        <Button size="sm" onClick={handleBulkApproveInvoices} className="rounded-2xl">
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button variant="destructive" size="sm" onClick={handleBulkBlockInvoices} className="rounded-2xl">
                          <XCircle className="h-4 w-4 mr-1" />
                          Block
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredInvoices.length === 0 ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <Card className="p-12 text-center rounded-3xl border-dashed">
                  <CheckCircle className="h-16 w-16 text-success mx-auto mb-6" />
                  <h3 className="text-xl font-semibold text-foreground mb-3">All Clear!</h3>
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    No flagged invoices to review.
                  </p>
                </Card>
              </motion.div>
            ) : (
              <div className="space-y-3">
                {/* Select All */}
                <div className="flex items-center gap-2 px-4 py-2">
                  <Checkbox
                    checked={selectedInvoices.size === filteredInvoices.length && filteredInvoices.length > 0}
                    onCheckedChange={selectAllInvoices}
                  />
                  <span className="text-sm text-muted-foreground">Select all</span>
                </div>

                <AnimatePresence mode="popLayout">
                  {filteredInvoices.map((invoice, idx) => (
                    <motion.div
                      key={invoice.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <Card className={cn(
                        "p-5 rounded-3xl transition-all hover:shadow-card-hover",
                        selectedInvoices.has(invoice.id) && "ring-2 ring-primary bg-primary/5"
                      )}>
                        <div className="flex items-start gap-4">
                          <Checkbox
                            checked={selectedInvoices.has(invoice.id)}
                            onCheckedChange={() => toggleInvoiceSelection(invoice.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-foreground">{invoice.vendor}</p>
                                  {getRiskBadge(invoice.risk_score)}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                  <span>#{invoice.invoice_no}</span>
                                  <span>•</span>
                                  <span>{formatDate(invoice.date)}</span>
                                </div>
                                {invoice.flag && (
                                  <Badge variant="outline" className="mt-2 text-xs rounded-xl">
                                    {invoice.flag}
                                  </Badge>
                                )}
                              </div>
                              <div className="font-bold text-foreground text-right shrink-0">
                                {formatCurrency(invoice.amount)}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" onClick={() => handleApproveInvoice(invoice.id)} className="h-9 rounded-2xl px-4">
                                <Check className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleBlockInvoice(invoice.id)} className="h-9 rounded-2xl px-4">
                                <XCircle className="h-4 w-4 mr-1" />
                                Block
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
